// src/hooks/useHeader.ts
import { useEffect, useRef } from 'react';
import { useHeaderContext, HeaderConfig } from '../contexts/HeaderContext';

/**
 * 簡單的淺層相等比較，專門用於 header 配置
 * 特殊處理 rightActions（比較長度而非引用）
 */
function shallowEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  for (const key of keys1) {
    // 特殊處理 rightActions（比較長度而非引用）
    if (key === 'rightActions') {
      const len1 = obj1[key]?.length || 0;
      const len2 = obj2[key]?.length || 0;
      if (len1 !== len2) return false;
    }
    // 特殊處理 allDayEvents 陣列（比較內容而非引用）
    else if (key === 'allDayEvents') {
      const arr1 = obj1[key] || [];
      const arr2 = obj2[key] || [];
      
      if (arr1.length !== arr2.length) return false;
      
      // 比較每個事件的 ID，避免深度比較
      for (let i = 0; i < arr1.length; i++) {
        if (arr1[i]?.id !== arr2[i]?.id) return false;
      }
    }
    // 特殊處理 subHeader（React 元素比較）
    else if (key === 'subHeader') {
      // SubHeader 通常包含交互元素和狀態，不做淺層比較優化
      // 總是觸發更新，讓 React 自行處理重新渲染和優化
      return false;
    }
    else if (obj1[key] !== obj2[key]) {
      return false;
    }
  }
  
  return true;
}

/**
 * useHeader Hook - 供頁面組件設置 Header 配置
 * 
 * 使用智能比較避免無限重新渲染循環
 * 
 * @param config - Header 配置對象
 * 
 * 使用範例：
 * ```typescript
 * useHeader({
 *   title: 'Today',
 *   rightActions: [<RefreshButton />, <DayCloseButton />]
 * });
 * ```
 */
export const useHeader = (config: HeaderConfig | null) => {
  const { setConfig } = useHeaderContext();
  const prevConfigRef = useRef<HeaderConfig | null>();
  
  useEffect(() => {
    // 只在配置真正改變時更新
    if (!shallowEqual(prevConfigRef.current, config)) {
      prevConfigRef.current = config;
      setConfig(config);
    }
  }, [config, setConfig]); // 移除清理函數中的 setConfig 調用

  // 組件卸載時的清理邏輯單獨處理
  useEffect(() => {
    return () => {
      // 組件卸載時清除
      setConfig(null);
      prevConfigRef.current = null;
    };
  }, [setConfig]); // setConfig 通常是穩定的，但 ESLint 要求包含
};